import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Branch, Department, IdentifierType, PatientSummary, ServiceItem, Staff } from '@/api/types'

const LONG = 5 * 60_000

/** Shared reference data. Cached for 5 minutes; mutations invalidate by key. */
export function useDepartments() {
  return useQuery({ queryKey: ['departments'], queryFn: () => api.get<Department[]>('/departments'), staleTime: LONG })
}

export function useBranches() {
  return useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/branches'), staleTime: LONG })
}

export function useStaff(clinicians = false) {
  return useQuery({
    queryKey: ['staff', clinicians],
    queryFn: () => api.get<Staff[]>('/staff', { clinicians_only: clinicians }),
    staleTime: LONG,
  })
}

export function useServices(category?: string) {
  return useQuery({
    queryKey: ['services', category ?? 'all'],
    queryFn: () => api.get<ServiceItem[]>('/services', { category }),
    staleTime: LONG,
  })
}

export function useIdentifierTypes() {
  return useQuery({ queryKey: ['identifier-types'], queryFn: () => api.get<IdentifierType[]>('/identifier-types'), staleTime: LONG })
}

export function useLookup<T extends { id: string }>(items: T[] | undefined) {
  const map = new Map<string, T>()
  items?.forEach((i) => map.set(i.id, i))
  return (id: string | null | undefined) => (id ? map.get(id) : undefined)
}

/** How a staff member is named everywhere: title + full name (e.g. "Dr. Anjali Aryal"). */
export function staffName(s: Pick<Staff, 'full_name'> & { title?: string | null } | null | undefined) {
  if (!s) return ''
  return `${s.title ? s.title + ' ' : ''}${s.full_name}`
}

/**
 * Pre-selects a patient passed in the URL (e.g. "Book appointment" from a patient's profile).
 * Applies once per id, whether the record comes from the network or is already cached — doing this
 * inside a queryFn silently skipped it whenever the profile page had cached the patient.
 */
/** `billing`: look the patient up as billing knows them (name, MRN, age / sex), for staff without patients.read. */
export function usePresetPatient(id: string | null | undefined, apply: (p: PatientSummary) => void, source?: 'billing') {
  const q = useQuery({ queryKey: ['patient', id, source], enabled: !!id, queryFn: () => api.get<PatientSummary>(source === 'billing' ? `/billing/patients/${id}` : `/patients/${id}`) })
  const applied = useRef<string | null>(null)
  const applyRef = useRef(apply)
  useEffect(() => {
    applyRef.current = apply
  })
  useEffect(() => {
    if (q.data && applied.current !== q.data.id) {
      applied.current = q.data.id
      applyRef.current(q.data)
    }
  }, [q.data])
  return q
}
